/**
 * 💳 Credit Manager Component
 * 🔧 OPTIMIZED: Event-driven credit updates only - NO polling
 * Listens for credit_operation events and refreshes balance once
 * Also invalidates TanStack Query cache for consistent data
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { invalidateCreditQueries } from '../lib/queryClient.js';

const CreditManager = ({ children }) => {
    const { user, isAuthenticated, refreshCredits } = useAuth();
    const [creditNotifications, setCreditNotifications] = useState([]);
    const [lastCredits, setLastCredits] = useState(user?.credits || 0);

    // Sync with user credits from AuthContext
    useEffect(() => {
        if (user?.credits !== undefined && user.credits !== lastCredits) {
            const creditChange = user.credits - lastCredits;
            if (lastCredits > 0 && creditChange !== 0) {
                handleCreditChange(user.credits, creditChange);
            }
            setLastCredits(user.credits);
        }
    }, [user?.credits]);

    // Force refresh function exposed globally
    const forceRefresh = useCallback(async () => {
        // Invalidate TanStack Query cache for credits
        invalidateCreditQueries();

        // Also refresh from AuthContext
        if (refreshCredits) {
            await refreshCredits();
        }
    }, [refreshCredits]);

    // Handle credit balance changes
    const handleCreditChange = (newCredits, creditChange) => {

        // Show notifications for significant credit changes
        if (Math.abs(creditChange) >= 5) {
            const notification = {
                id: Date.now(),
                type: creditChange > 0 ? 'credit_added' : 'credit_used',
                amount: Math.abs(creditChange),
                newBalance: newCredits,
                timestamp: new Date()
            };

            setCreditNotifications(prev => [notification, ...prev.slice(0, 4)]); // Keep last 5

            // Auto-remove notification after 5 seconds
            setTimeout(() => {
                setCreditNotifications(prev => prev.filter(n => n.id !== notification.id));
            }, 5000);
        }

        // Show low credit warnings
        if (newCredits <= 10 && creditChange < 0) {
            window.showToast(
                `⚠️ Low credit warning: ${newCredits} credits remaining`,
                'error'
            );
        }

        // Critical credit warning
        if (newCredits <= 5 && creditChange < 0) {
            window.showToast(
                `🚨 Critical: Only ${newCredits} credits left! Consider purchasing more.`,
                'error'
            );
        }
    };

    // Force refresh credits after API operations
    useEffect(() => {
        const handleApiOperation = (event) => {
            if (event.detail?.type === 'credit_operation') {
                forceRefresh();
            }
        };

        window.addEventListener('api_operation', handleApiOperation);
        
        return () => {
            window.removeEventListener('api_operation', handleApiOperation);
        };
    }, [forceRefresh]);

    // Global function to trigger credit refresh
    useEffect(() => {
        window.refreshCredits = forceRefresh;
        
        return () => {
            delete window.refreshCredits;
        };
    }, [forceRefresh]);

    // Debug info removed for production

    // Render credit change notifications
    const renderNotifications = () => {
        if (creditNotifications.length === 0) return null;

        return (
            <div className="fixed top-20 right-4 z-50 space-y-2">
                {creditNotifications.map((notification) => (
                    <div
                        key={notification.id}
                        className={`
                            bg-white shadow-lg rounded-lg p-3 border-l-4 
                            ${notification.type === 'credit_added' 
                                ? 'border-green-500 bg-green-50' 
                                : 'border-blue-500 bg-blue-50'
                            }
                            animate-fade-in-down
                        `}
                    >
                        <div className="flex items-center">
                            <div className="text-lg mr-2">
                                {notification.type === 'credit_added' ? '💰' : '⚡'}
                            </div>
                            <div>
                                <div className="text-sm font-medium text-gray-900">
                                    {notification.type === 'credit_added' 
                                        ? `+${notification.amount} credits added`
                                        : `${notification.amount} credits used`
                                    }
                                </div>
                                <div className="text-xs text-gray-500">
                                    Balance: {notification.newBalance} credits
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <>
            {children}
            {renderNotifications()}
        </>
    );
};

export default CreditManager;